import {
  Box,
  Checkbox,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type ArchivedProjectDataFragment,
  type FinishedProjectDataFragment,
  RequestForInvoicingTypeEnum,
  useArchiveProjectMutation,
  useDeleteProjectMutation,
  useInvoiceProjectByTypeMutation,
  usePinProjectMutation,
  useProformaInvoiceProjectMutation,
  useReopenProjectMutation,
  useUnarchiveProjectMutation,
  useUnpinProjectMutation,
} from "@src/__generated__/graphql";
import type { ProjectModel } from "@src/components/modules/projects/detail/models";
import { Banner, MenuItemLink, ModalConfirm } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { DuplicateProjectModal } from "@src/widgets/DuplicateProjectModal/DuplicateProjectModal";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useRouter } from "next/router";
import {
  Fragment,
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import ModalFinishProject, {
  type ModalFinishProjectProps,
} from "../Modals/ModalFinishProject";
import { PopoverProjectActionsStore } from "./store";

type Project = Pick<
  ProjectModel,
  | "id"
  | "is_pinned"
  | "is_starred"
  | "projectManager"
  | "isTemplate"
  | "title"
  | "code"
  | "billable"
> &
  Partial<Pick<ProjectModel, "archived_at" | "cloudUrl" | "finished_at">> & {
    otherProjectManagers?:
      | Pick<ProjectModel["otherProjectManagers"][0], "id">[]
      | null;
  };
export type PopoverProjectActionsProps = {
  project: Project;

  openModalOnMount?: "finish";
  hideActions?: ("pin-unpin" | "edit" | "star-unstar")[];

  /** Called when any action is completed except of redirects */
  onAction?: () => void;

  /** Used by archive/unarchive project mutation */
  onArchived?: (project: ArchivedProjectDataFragment) => void;

  /** Used by finish/reopen project mutation */
  onFinished?: (project: FinishedProjectDataFragment) => void;

  onClosedModalFinish?: ModalFinishProjectProps["onClosed"];

  activator: (isLoading?: boolean) => ReactNode;
} & Pick<
  ModalFinishProjectProps,
  "onCompleteOurWork" | "onCompleteExpense" | "onCreditRedistributed"
>;

type ConfirmAction =
  | "createProforma"
  | "createCreditNote"
  | "archive"
  | "unarchive"
  | "delete";

const PopoverProjectActions: FunctionComponent<PopoverProjectActionsProps> = ({
  project,
  openModalOnMount,
  hideActions,
  onAction,
  onArchived,
  onFinished,
  onClosedModalFinish,
  onCompleteOurWork,
  onCompleteExpense,
  onCreditRedistributed,
  ...popoverProps
}) => {
  const router = useRouter();
  const appStore = useStore();

  const store = useLocalObservable(
    () => new PopoverProjectActionsStore(appStore, onAction),
  );

  const finishProjectDialog = useDisclosure();
  const confirmDialog = useDisclosure();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(
    null,
  );

  const openConfirmDialog = (action: ConfirmAction) => {
    setConfirmAction(action);
    confirmDialog.onOpen();
  };

  const closeConfirmDialog = () => {
    confirmDialog.onClose();
    setConfirmAction(null);
  };

  useEffect(() => {
    if (openModalOnMount === "finish") {
      finishProjectDialog.onOpen();
    }
  }, [openModalOnMount]);

  const [pinProject] = usePinProjectMutation({
    variables: { id: project.id },
    ...onError(),
    onCompleted() {
      onAction?.();
    },
  });

  const [unpinProject] = useUnpinProjectMutation({
    variables: { id: project.id },
    ...onError(),
    onCompleted() {
      onAction?.();
    },
  });

  const [deleteProject, deleteProjectRequest] = useDeleteProjectMutation({
    variables: { id: project.id },
    ...onError(),
    onCompleted() {
      closeConfirmDialog();
      if (window.location.pathname === "/projects/detail") {
        router.push("/projects");
      } else {
        window.location.reload();
      }
    },
  });

  const [archiveProject, archiveProjectRequest] = useArchiveProjectMutation({
    variables: { id: project.id },
    ...onError(),
    onCompleted({ archiveProject }) {
      closeConfirmDialog();
      onArchived?.(archiveProject);
      onAction?.();
    },
  });

  const [unarchiveProject, unarchiveProjectRequest] =
    useUnarchiveProjectMutation({
      variables: { id: project.id },
      ...onError(),
      onCompleted({ unarchiveProject }) {
        closeConfirmDialog();
        onArchived?.(unarchiveProject);
        onAction?.();
      },
    });

  const [createInvoice, createInvoiceRequest] = useInvoiceProjectByTypeMutation(
    {
      variables: {
        id: project.id,
        invoice_type: RequestForInvoicingTypeEnum.Invoice,
      },
      ...onError(),
      onCompleted(data) {
        if (data.invoiceProject?.id) {
          router.push({
            pathname: "/invoices/for-invoicing/edit",
            query: { id: data.invoiceProject?.id },
          });
        }
      },
    },
  );

  const [createProforma, createProformaRequest] =
    useProformaInvoiceProjectMutation({
      variables: { projectId: project.id },
      ...onError(),
      onCompleted(data) {
        if (data.proformaInvoiceProject?.id) {
          closeConfirmDialog();
          router.push({
            pathname: "/invoices/for-invoicing/edit",
            query: { id: data.proformaInvoiceProject.id },
          });
        }
      },
    });

  const [createCreditNote, createCreditNoteRequest] =
    useInvoiceProjectByTypeMutation({
      variables: {
        id: project.id,
        invoice_type: RequestForInvoicingTypeEnum.CreditNote,
      },
      ...onError(),
      onCompleted(data) {
        if (data.invoiceProject?.id) {
          closeConfirmDialog();
          router.push({
            pathname: "/invoices/for-invoicing/edit",
            query: { id: data.invoiceProject?.id },
          });
        }
      },
    });

  const [reopenProject] = useReopenProjectMutation({
    variables: { id: project.id },
    ...onError(),
    onCompleted({ reopenProject }) {
      onFinished?.(reopenProject);
      onAction?.();
    },
  });

  const projectManagerIds = [
    project.projectManager.id,
    ...(project?.otherProjectManagers || []).map((i) => i.id),
  ];

  function getConfirmLoading(): boolean {
    switch (confirmAction) {
      case "createProforma":
        return createProformaRequest.loading;
      case "createCreditNote":
        return createCreditNoteRequest.loading;
      case "archive":
        return archiveProjectRequest.loading;
      case "unarchive":
        return unarchiveProjectRequest.loading;
      case "delete":
        return deleteProjectRequest.loading;
      default:
        return false;
    }
  }

  function onConfirmAction() {
    switch (confirmAction) {
      case "createProforma":
        return createProforma();
      case "createCreditNote":
        return createCreditNote();
      case "archive":
        return archiveProject();
      case "unarchive":
        return unarchiveProject();
      case "delete":
        return deleteProject();
      default:
        return undefined;
    }
  }

  return (
    <Fragment>
      <Menu isLazy>
        <MenuButton w="full">
          {popoverProps.activator(createInvoiceRequest.loading)}
        </MenuButton>
        {!project.isTemplate ? (
          <Portal>
            <MenuList>
              <EditButton
                hideAction={hideActions?.includes("edit")}
                project={project}
                projectManagerIds={projectManagerIds}
              />
              {!hideActions?.includes("pin-unpin") &&
                (can("project_pin_all", "project_pin_team") ||
                  can({
                    do: "project_pin_own",
                    eval: (user) => projectManagerIds.includes(user.id),
                  })) &&
                (project.is_pinned ? (
                  <MenuItem
                    icon={<Icon name="pin-01" w="5" h="5" color="grey.500" />}
                    onClick={() => unpinProject()}
                  >
                    <Trans>Unpin</Trans>
                  </MenuItem>
                ) : (
                  <MenuItem
                    icon={<Icon name="pin-01" w="5" h="5" color="grey.500" />}
                    onClick={() => pinProject()}
                  >
                    <Trans>Pin</Trans>
                  </MenuItem>
                ))}

              {!hideActions?.includes("star-unstar") && project.is_starred ? (
                <MenuItem
                  icon={<Icon name="star-01" w="5" h="5" color="grey.500" />}
                  onClick={() =>
                    store.starProjectModalState.onOpen({
                      id: project.id,
                      action: "unstar",
                    })
                  }
                >
                  <Trans>Unstar</Trans>
                </MenuItem>
              ) : (
                <MenuItem
                  icon={<Icon name="star-01" w="5" h="5" color="grey.500" />}
                  onClick={() =>
                    store.starProjectModalState.onOpen({
                      id: project.id,
                      action: "star",
                    })
                  }
                >
                  <Trans>Star</Trans>
                </MenuItem>
              )}

              <DuplicateButton
                project={project}
                projectManagerIds={projectManagerIds}
              />
              {!(project.finished_at || project.archived_at) &&
                project.billable &&
                can(
                  "requestForInvoicing_create_all",
                  "requestForInvoicing_create_own",
                  "requestForInvoicing_create_team",
                ) && (
                  <Fragment>
                    <MenuItem
                      icon={
                        <Icon name="file-06" w="5" h="5" color="grey.500" />
                      }
                      onClick={() => createInvoice()}
                    >
                      <Trans>Invoice</Trans>
                    </MenuItem>
                    <MenuItem
                      icon={
                        <Icon name="file-06" w="5" h="5" color="grey.500" />
                      }
                      onClick={() => openConfirmDialog("createProforma")}
                    >
                      <Trans>Proforma</Trans>
                    </MenuItem>
                    <MenuItem
                      icon={
                        <Icon name="file-06" w="5" h="5" color="grey.500" />
                      }
                      onClick={() => openConfirmDialog("createCreditNote")}
                    >
                      <Trans>Credit note</Trans>
                    </MenuItem>
                  </Fragment>
                )}
              {/* Archive/Unarchive only for finished project */}
              {!!project.finished_at &&
                (can("project_archive_all", "project_archive_team") ||
                  can({
                    do: "project_archive_own",
                    eval: (user) => projectManagerIds.includes(user.id),
                  })) &&
                (project.archived_at ? (
                  <MenuItem
                    icon={
                      <Icon name="reverse-left" w="5" h="5" color="grey.500" />
                    }
                    onClick={() => openConfirmDialog("unarchive")}
                  >
                    <Trans>Unarchive</Trans>
                  </MenuItem>
                ) : (
                  <MenuItem
                    icon={<Icon name="archive" w="5" h="5" color="grey.500" />}
                    onClick={() => openConfirmDialog("archive")}
                  >
                    <Trans>Archive</Trans>
                  </MenuItem>
                ))}
              {/* Reopen/Finish only for non-archived project */}
              {!project.archived_at &&
                (project.finished_at
                  ? (can("project_reopen_all", "project_reopen_team") ||
                      can({
                        do: "project_reopen_own",
                        eval: (user) => projectManagerIds.includes(user.id),
                      })) && (
                      <MenuItem
                        icon={
                          <Icon
                            name="reverse-left"
                            w="5"
                            h="5"
                            color="grey.500"
                          />
                        }
                        onClick={() => reopenProject()}
                      >
                        <Trans>Reopen</Trans>
                      </MenuItem>
                    )
                  : (can("project_finish_all", "project_finish_team") ||
                      can({
                        do: "project_finish_own",
                        eval: (user) => projectManagerIds.includes(user.id),
                      })) && (
                      <MenuItem
                        icon={
                          <Icon name="stars-02" w="5" h="5" color="grey.500" />
                        }
                        onClick={finishProjectDialog.onOpen}
                      >
                        <Trans>Finish</Trans>
                      </MenuItem>
                    ))}
              <DeleteButton
                onClick={() => openConfirmDialog("delete")}
                projectManagerIds={projectManagerIds}
              />
            </MenuList>
          </Portal>
        ) : (
          <Portal>
            <MenuList>
              <EditButton
                project={project}
                projectManagerIds={projectManagerIds}
              />
              <DuplicateButton
                project={project}
                projectManagerIds={projectManagerIds}
              />
              <DeleteButton
                onClick={() => openConfirmDialog("delete")}
                projectManagerIds={projectManagerIds}
              />
            </MenuList>
          </Portal>
        )}
      </Menu>

      <ModalConfirm
        closeOnConfirm={false}
        isLoading={getConfirmLoading()}
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={onConfirmAction}
      >
        {confirmAction === "delete" && (
          <Fragment>
            {project?.cloudUrl && project.cloudUrl.length > 0 && (
              <Banner
                title={t`When you delete the project, the corresponding folder on SharePoint will be automatically removed.`}
                status="warning"
                mb="4"
              />
            )}
            <Box>
              <Trans>Are you sure?</Trans>
            </Box>
          </Fragment>
        )}
      </ModalConfirm>
      <ModalConfirm
        closeOnConfirm={false}
        isOpen={store.starProjectModalState.isOpen}
        onClose={store.starProjectModalState.onClose}
        onConfirm={() =>
          store.confirmStarAction(
            (nextState) => (project.is_starred = nextState),
          )
        }
        isLoading={store.isStarToggling}
        heading={
          store.isActionStar
            ? t`Star project`
            : store.isActionUnstar
              ? t`Unstar project`
              : undefined
        }
        message={
          <VStack alignItems="start" spacing={2}>
            <Checkbox
              onChange={({ target }) => {
                if (store.starProjectModalState.additionalData) {
                  store.starProjectModalState.additionalData.withMyTasks =
                    target.checked;
                }
              }}
            >
              {store.isActionStar ? (
                <Trans>
                  Also star all my tasks related to selected projects
                </Trans>
              ) : store.isActionUnstar ? (
                <Trans>
                  Also unstar all my tasks related to selected projects
                </Trans>
              ) : null}
            </Checkbox>
            <p>
              <Trans>Are you sure?</Trans>
            </p>
          </VStack>
        }
      />
      {project && (
        <ModalFinishProject
          id={project.id}
          onCompleteOurWork={onCompleteOurWork}
          onCompleteExpense={onCompleteExpense}
          onCreditRedistributed={onCreditRedistributed}
          onFinished={(data) => {
            onFinished?.(data);
            onAction?.();
          }}
          onClosed={(isDirty) => {
            onClosedModalFinish?.(isDirty);
            isDirty && onAction?.();
          }}
          {...finishProjectDialog}
        />
      )}
    </Fragment>
  );
};

type DuplicateButtonProps = {
  project: Project;
  projectManagerIds: string[];
};
const DuplicateButton = observer(function DuplicateButton({
  project,
  projectManagerIds,
}: DuplicateButtonProps) {
  const { UIStore } = useStore();
  const { push } = useRouter();
  return (
    (can("project_duplicate_all", "project_duplicate_team") ||
      can({
        do: "project_duplicate_own",
        eval: (user) => projectManagerIds.includes(user.id),
      })) && (
      <MenuItem
        icon={<Icon name="copy-02" w="5" h="5" color="grey.500" />}
        onClick={() => {
          UIStore.dialogs.openModal({
            content: (
              <DuplicateProjectModal
                projectId={project.id}
                onSubmit={(duplicatedProject) => {
                  UIStore.toast({
                    title: t`The project has been duplicated`,
                    status: "success",
                  });
                  if (duplicatedProject.id) {
                    push({
                      pathname: "/projects/edit",
                      query: {
                        id: duplicatedProject.id,
                        from_project_id: project.id,
                      },
                    });
                  }
                }}
              />
            ),
          });
        }}
      >
        <Trans>Duplicate</Trans>
      </MenuItem>
    )
  );
});

type DeleteButtonProps = {
  projectManagerIds: string[];
  onClick: () => void;
};
const DeleteButton = observer(function DeleteButton({
  projectManagerIds,
  onClick,
}: DeleteButtonProps) {
  return (
    (can("project_delete_all", "project_delete_team") ||
      can({
        do: "project_delete_own",
        eval: (user) => projectManagerIds.includes(user.id),
      })) && (
      <MenuItem
        icon={<Icon name="trash-03" w="5" h="5" color="grey.500" />}
        onClick={onClick}
      >
        <Trans>Delete</Trans>
      </MenuItem>
    )
  );
});

type EditButtonProps = {
  project: Project;
  hideAction?: boolean | undefined;
  projectManagerIds: string[];
};
const EditButton = observer(function EditButton({
  project,
  projectManagerIds,
  hideAction,
}: EditButtonProps) {
  return (
    !hideAction &&
    (can("project_update_all", "project_update_team") ||
      can({
        do: "project_update_own",
        eval: (user) => projectManagerIds.includes(user.id),
      })) && (
      <MenuItemLink
        icon={<Icon name="edit-02" w="5" h="5" color="grey.500" />}
        href={{
          pathname: "/projects/edit",
          query: {
            id: project.id,
          },
        }}
      >
        {project.isTemplate ? (
          <Trans>Edit template</Trans>
        ) : (
          <Trans>Edit</Trans>
        )}
      </MenuItemLink>
    )
  );
});

export default observer(PopoverProjectActions);
